import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import { PaymentMethod } from '../../../../core/models/payment/payment-method';
import { AccountService } from '../../../../core/services/api/account.service';
import { WalletService } from '../../../../core/services/api/mysec/wallet.service';
import { DoubleDecimalPattern, MinimumWithdrawAmount } from '../../../../core/services/constant';
import { ErrorService } from '../../../../core/services/error.service';
import { BlockUiService } from '../../../../shared/services/blockUi/block-ui.service';
import { ConfirmWithdrawComponent } from '../confirm-withdraw/confirm-withdraw.component';

@Component({
    selector: 'app-withdraw',
    templateUrl: './withdraw.component.html',
    styleUrls: ['./withdraw.component.scss'],
    standalone: false
})
export class WithdrawComponent implements OnInit {
  balance: number;
  // FRC-858: server/wallet-derived figures the popup leads with.
  // withdrawableBalance = Balance - LockedAmount; the FE does no further balance math.
  withdrawableBalance: number;
  pendingAmount: number = 0;
  form: UntypedFormGroup;
  showFormError: boolean = false;
  paymentMethod: PaymentMethod;
  isLoading = false;

  MinimumWithdrawAmount = MinimumWithdrawAmount;
  DoubleDecimalPattern = DoubleDecimalPattern;

  // FRC-858: block cleanly when there is nothing meaningful to withdraw.
  get isBelowMinimum(): boolean {
    return (this.withdrawableBalance ?? 0) < MinimumWithdrawAmount;
  }

  /**
   * FRC-858: the amount control is a text input (`digitOnly`), and `setMax()` writes a
   * `toFixed(2)` string into it, so `form.value.amount` is a string on every path. Coerce
   * once here so the confirm modal and the withdraw payload both get a real number.
   */
  private get enteredAmount(): number {
    return +this.form.value.amount;
  }

  // FRC-858: the entered amount is below the RM1.00 minimum (field is usable).
  get isAmountBelowMinimum(): boolean {
    const c = this.form?.controls?.['amount'];
    return !!c && c.dirty && c.hasError('min');
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modal: NgbActiveModal,
    private accountService: AccountService,
    private modalService: NgbModal,
    private errorService: ErrorService,
    private blockUiService: BlockUiService,
    private walletService: WalletService,
  ) {}

  ngOnInit() {
    this.normaliseBalances();
    this.buildForm();
    this.getProfilePaymentMethods();
  }

  /**
   * FRC-858: buildForm() bakes `withdrawableBalance` into both the disabled state and the
   * max validator, so it must be a real number by then. A caller that only sets `balance`
   * would otherwise disable the field outright and leave the cap validating against
   * undefined. Fall back to balance - pendingAmount and clamp at 0.
   */
  private normaliseBalances(): void {
    this.balance = Math.max(0, this.balance ?? 0);
    this.pendingAmount = Math.max(0, this.pendingAmount ?? 0);
    this.withdrawableBalance = Math.max(
      0,
      +(this.withdrawableBalance ?? this.balance - this.pendingAmount).toFixed(2),
    );
  }

  buildForm() {
    this.form = this.formBuilder.group({
      amount: [
        { value: null, disabled: this.isBelowMinimum },
        [
          Validators.required,
          // FRC-858: cap at the withdrawable figure, not the gross balance.
          Validators.max(this.withdrawableBalance),
          Validators.min(MinimumWithdrawAmount),
          Validators.pattern(DoubleDecimalPattern),
        ],
      ],
    });
  }

  setMax() {
    if (this.isBelowMinimum) {
      return;
    }
    this.form.controls.amount.setValue(this.withdrawableBalance.toFixed(2));
  }

  submit() {
    if (this.form.invalid) {
      this.showFormError = true;
      return;
    }
    this.displayConfirm();
  }

  close() {
    this.modal.dismiss();
  }

  getProfilePaymentMethods() {
    this.isLoading = true;

    this.accountService
      .getProfilePaymentMethods()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data: any) => {
          this.paymentMethod = data;
        },
        (err) => {
          this.errorService.showError(err);
        },
      );
  }

  displayConfirm() {
    const option: any = {
      size: 'md',
      centered: true,
      windowClass: 'modal-550',
      backdropClass: 'backdrop-popup',
    };
    const modalRef = this.modalService.open(ConfirmWithdrawComponent, option);
    modalRef.componentInstance.amount = this.enteredAmount;
    // FRC-858: project the new balance + its held portion on the confirm step.
    modalRef.componentInstance.balance = this.balance;
    modalRef.componentInstance.pendingAmount = this.pendingAmount;
    modalRef.result.then(
      () => {
        this.withdrawWallet();
      },
      () => {},
    );
  }

  withdrawWallet() {
    this.blockUiService.open();
    this.paymentMethod.amount = this.enteredAmount;

    this.walletService
      .withdrawWallet(this.paymentMethod)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe(
        (data) => {
          this.modal.close();
        },
        (err) => {
          this.errorService.showError(err);
        },
      );
  }
}
